import { useState } from 'react';
import type { FormEvent } from 'react';
import { login } from '../services/authService';
import { useAuth } from '../hooks/useAuth';
import Button from '../../../components/Button/Button';
import Form, { FormFeedback } from '../../../components/Form/Form';
import Input from '../../../components/Form/Input';
import MicroUnit from '../../../components/Modular/MicroUnit';

interface LoginFormProps {
  onSuccess: () => void;
}

export const LoginForm = ({ onSuccess }: LoginFormProps) => {
  const { refreshSession } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const [error, setError] = useState<string | null>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    setError(null);
    setIsSubmitting(true);

    try {
      await login({
        email,
        password,
      });

      const authorized = await refreshSession();

      if (!authorized) {
        setError('This account does not have administrator access.');

        return;
      }

      onSuccess();
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Login failed.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Form variant="stacked" onSubmit={handleSubmit}>
      <Input
        id="email"
        label="Email"
        type="email"
        variant="auth"
        value={email}
        onChange={(event) => setEmail(event.target.value)}
        autoComplete="email"
        required
      />

      <Input
        id="password"
        label="Password"
        type="password"
        variant="auth"
        value={password}
        onChange={(event) => setPassword(event.target.value)}
        autoComplete="current-password"
        required
      />

      {error && <FormFeedback role="alert">{error}</FormFeedback>}

      <Button
        marginTop="md"
        variant="authPrimary"
        type="submit"
        disabled={isSubmitting}
      >
        <MicroUnit as="p">
          {isSubmitting ? 'Signing in...' : 'Sign in'}
        </MicroUnit>
      </Button>
    </Form>
  );
};

export default LoginForm;
